/**
 * CS Category Service
 *
 * Two surfaces, deliberately kept apart:
 *  - Templates (super-admin only): the shared catalog every account/BU copy is seeded and
 *    propagated from. Unscoped — never takes a `bu_id`.
 *  - BU-scoped `/cs-categories`: the account's own copies, scoped with `buScopeParams` /
 *    `withBuScope` the same way guardrails are (see `src/lib/business-unit-scope.ts`).
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type {
  ApiResponse,
  CSCategory,
  CSCategoryPropagateAction,
  CSCategoryPropagateScope,
  CSCategoryPropagatePreviewResult,
  CSCategoryPropagateApplyResult,
  CSCategoryPropagateTarget
} from '@/types';

interface CSCategoryResponseDto {
  _id: string;
  id: string;
  name?: string;
  definition?: string;
  category?: string;
  subcategory?: string;
  rule_name?: string;
  ticket_category?: string;
  status?: 'published';
  accountId?: number | null;
  buId?: number | null;
  isEnabled?: boolean;
  templateVersion?: number;
  isCustomized?: boolean;
}

interface CSCategoriesPagedResponseDto {
  categories: CSCategoryResponseDto[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CSCategoryQueryParams {
  name?: string;
  category?: string;
  subcategory?: string;
  page?: number;
  limit?: number;
}

export interface CSCategoriesPagedResult {
  categories: CSCategory[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CSCategoryFilterOptionsResult {
  categories: string[];
  subcategories: string[];
}

export interface CSCategoryScope {
  bu_id?: string;
}

export interface CreateCSCategoryPayload {
  id: string;
  name?: string;
  definition?: string;
  category?: string;
  subcategory?: string;
  rule_name?: string;
  ticket_category?: string;
}

export interface UpdateCSCategoryPayload {
  id?: string;
  definition?: string;
  category?: string;
  subcategory?: string;
  rule_name?: string;
  ticket_category?: string;
}

export interface PropagateRequestPayload {
  action: CSCategoryPropagateAction;
  scope: CSCategoryPropagateScope;
  buIds?: number[];
}

function formatIdAsLabel(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function mapFromDto(dto: CSCategoryResponseDto): CSCategory {
  return {
    id: dto.id,
    name: dto.id ? formatIdAsLabel(dto.id) : (dto.subcategory || dto.rule_name || dto.name || ''),
    definition: dto.definition || '',
    category: dto.category || '',
    subcategory: dto.subcategory || '',
    rule_name: dto.rule_name || '',
    ticket_category: dto.ticket_category ?? undefined,
    status: dto.status ?? 'published',
    accountId: dto.accountId ?? null,
    buId: dto.buId ?? null,
    isEnabled: dto.isEnabled ?? true,
    templateVersion: dto.templateVersion,
    isCustomized: dto.isCustomized ?? false
  };
}

export const csCategoryService = {
  // ==========================================================================
  // BU-scoped (account's own intents)
  // ==========================================================================

  async getCategories(scope: CSCategoryScope = {}): Promise<CSCategory[]> {
    const response = await api.get<ApiResponse<CSCategoriesPagedResponseDto>>(
      withBuScope(API_ENDPOINTS.CS_CATEGORIES.BASE, scope),
      { params: { limit: '1000' } }
    );
    return (response.data.data.categories || []).map(mapFromDto);
  },

  async getCategoriesPaged(
    queryParams: CSCategoryQueryParams = {},
    scope: CSCategoryScope = {}
  ): Promise<CSCategoriesPagedResult> {
    const response = await api.get<ApiResponse<CSCategoriesPagedResponseDto>>(
      withBuScope(API_ENDPOINTS.CS_CATEGORIES.BASE, scope),
      { params: queryParams }
    );
    const { categories, total, page, limit, totalPages } = response.data.data;
    return { categories: (categories || []).map(mapFromDto), total, page, limit, totalPages };
  },

  async getFilterOptions(scope: CSCategoryScope = {}): Promise<CSCategoryFilterOptionsResult> {
    const response = await api.get<ApiResponse<CSCategoryFilterOptionsResult>>(
      withBuScope(API_ENDPOINTS.CS_CATEGORIES.OPTIONS, scope)
    );
    return response.data.data;
  },

  async createCategory(data: CreateCSCategoryPayload, scope: CSCategoryScope = {}): Promise<CSCategory> {
    const response = await api.post<ApiResponse<{ category: CSCategoryResponseDto }>>(
      withBuScope(API_ENDPOINTS.CS_CATEGORIES.BASE, scope),
      data
    );
    return mapFromDto(response.data.data.category);
  },

  async updateCategory(
    id: string,
    data: UpdateCSCategoryPayload,
    scope: CSCategoryScope = {}
  ): Promise<CSCategory> {
    const response = await api.put<ApiResponse<{ category: CSCategoryResponseDto }>>(
      withBuScope(API_ENDPOINTS.CS_CATEGORIES.BY_ID(id), scope),
      data
    );
    return mapFromDto(response.data.data.category);
  },

  async setCategoryEnabled(id: string, isEnabled: boolean, scope: CSCategoryScope = {}): Promise<CSCategory> {
    const response = await api.patch<ApiResponse<{ category: CSCategoryResponseDto }>>(
      withBuScope(API_ENDPOINTS.CS_CATEGORIES.ENABLED(id), scope),
      { isEnabled }
    );
    return mapFromDto(response.data.data.category);
  },

  async deleteCategory(id: string, scope: CSCategoryScope = {}): Promise<void> {
    await api.delete(withBuScope(API_ENDPOINTS.CS_CATEGORIES.BY_ID(id), scope));
  },

  // ==========================================================================
  // Templates (super-admin only, unscoped)
  // ==========================================================================

  async getTemplates(queryParams: CSCategoryQueryParams = {}): Promise<CSCategoriesPagedResult> {
    const response = await api.get<ApiResponse<CSCategoriesPagedResponseDto>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.BASE,
      { params: queryParams }
    );
    const { categories, total, page, limit, totalPages } = response.data.data;
    return { categories: (categories || []).map(mapFromDto), total, page, limit, totalPages };
  },

  async getTemplateOptions(): Promise<CSCategoryFilterOptionsResult> {
    const response = await api.get<ApiResponse<CSCategoryFilterOptionsResult>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.OPTIONS
    );
    return response.data.data;
  },

  async createTemplate(data: CreateCSCategoryPayload): Promise<CSCategory> {
    const response = await api.post<ApiResponse<{ category: CSCategoryResponseDto }>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.BASE,
      data
    );
    return mapFromDto(response.data.data.category);
  },

  async updateTemplate(id: string, data: UpdateCSCategoryPayload): Promise<CSCategory> {
    const response = await api.put<ApiResponse<{ category: CSCategoryResponseDto }>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.BY_ID(id),
      data
    );
    return mapFromDto(response.data.data.category);
  },

  async setTemplateEnabled(id: string, isEnabled: boolean): Promise<CSCategory> {
    const response = await api.patch<ApiResponse<{ category: CSCategoryResponseDto }>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.ENABLED(id),
      { isEnabled }
    );
    return mapFromDto(response.data.data.category);
  },

  async deleteTemplate(id: string): Promise<void> {
    await api.delete(API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.BY_ID(id));
  },

  async getPropagateBusinessUnits(): Promise<CSCategoryPropagateTarget[]> {
    const response = await api.get<ApiResponse<CSCategoryPropagateTarget[]>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.BUSINESS_UNITS
    );
    return response.data.data ?? [];
  },

  async previewPropagate(id: string, payload: PropagateRequestPayload): Promise<CSCategoryPropagatePreviewResult> {
    const response = await api.post<ApiResponse<CSCategoryPropagatePreviewResult>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.PROPAGATE_PREVIEW(id),
      payload
    );
    return response.data.data;
  },

  async applyPropagate(id: string, payload: PropagateRequestPayload): Promise<CSCategoryPropagateApplyResult> {
    const response = await api.post<ApiResponse<CSCategoryPropagateApplyResult>>(
      API_ENDPOINTS.CS_CATEGORIES.TEMPLATES.PROPAGATE(id),
      payload
    );
    return response.data.data;
  }
};
